import { useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

// PRD.202 — Pharmacy medicine label print.
//
// Uses the same iframe-based isolation as `PrescriptionPrintMount` so the
// host page is not affected during print and the label can declare its
// own page size (`@page { size: 100mm 70mm }`) without colliding with
// other in-page print stylesheets (e.g. the A4 prescription print).
//
// Reprint and preview are pure reads — never mutates DB state, never
// writes to `stock_movements`.

export interface PharmacyLabelData {
  patientName: string;
  medicineName: string;
  sku?: string | null;
  instructions?: string | null;
  qty?: number | null;
  unit?: string | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  dispensedAt?: string | null;
  dispenseNumber?: string | null;
  clinicName?: string | null;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateOnly(iso: string | null | undefined): string {
  if (!iso) return '';
  const datePart = iso.length >= 10 ? iso.slice(0, 10) : iso;
  const d = new Date(datePart);
  if (Number.isNaN(d.getTime())) return datePart;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function LabelBody({ data }: { data: PharmacyLabelData }) {
  const qtyLine =
    data.qty != null
      ? `${data.qty}${data.unit ? ` ${data.unit}` : ''}`
      : data.unit
        ? data.unit
        : '';
  return (
    <div
      style={{
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        color: '#111827',
        padding: '6mm 6mm',
        fontSize: 11,
        lineHeight: 1.35,
        boxSizing: 'border-box',
        width: '100mm',
      }}
    >
      {data.clinicName && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#374151',
            borderBottom: '1px solid #d1d5db',
            paddingBottom: 3,
            marginBottom: 5,
          }}
        >
          {data.clinicName}
        </div>
      )}

      <div style={{ marginBottom: 4 }}>
        <span style={{ color: '#6b7280' }}>Patient:</span>{' '}
        <span style={{ fontWeight: 600 }}>{data.patientName || '—'}</span>
      </div>

      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
          {data.medicineName || 'Medicine'}
        </div>
        {data.sku && <div style={{ fontSize: 9, color: '#6b7280' }}>SKU: {data.sku}</div>}
      </div>

      {data.instructions && (
        <div style={{ marginBottom: 4 }}>
          <span style={{ color: '#6b7280' }}>Sig:</span> {data.instructions}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '2px 8px',
          marginTop: 4,
          fontSize: 10,
        }}
      >
        {qtyLine && (
          <div>
            <span style={{ color: '#6b7280' }}>Qty:</span> <strong>{qtyLine}</strong>
          </div>
        )}
        {data.batchNumber && (
          <div>
            <span style={{ color: '#6b7280' }}>Batch:</span> {data.batchNumber}
          </div>
        )}
        {data.expiryDate && (
          <div>
            <span style={{ color: '#6b7280' }}>Expiry:</span> {fmtDateOnly(data.expiryDate)}
          </div>
        )}
        {data.dispensedAt && (
          <div>
            <span style={{ color: '#6b7280' }}>Dispensed:</span> {fmtDate(data.dispensedAt)}
          </div>
        )}
      </div>

      {data.dispenseNumber && (
        <div
          style={{
            marginTop: 5,
            fontSize: 8,
            color: '#9ca3af',
            textAlign: 'right',
          }}
        >
          {data.dispenseNumber}
        </div>
      )}
    </div>
  );
}

/**
 * When `data` becomes non-null, the label is rendered to static HTML and
 * pushed into a hidden iframe whose `@page` rule sets the small label
 * size. The iframe's `print()` is invoked — fully isolated from the host
 * page — and the iframe is removed once `afterprint` fires (or after the
 * watchdog times out).
 */
export function PharmacyLabelPrintMount({
  data,
  onAfterPrint,
}: {
  data: PharmacyLabelData | null;
  onAfterPrint: () => void;
}) {
  const lastFiredKey = useRef<string | null>(null);

  useEffect(() => {
    if (!data) {
      lastFiredKey.current = null;
      return;
    }
    // Use the dispense identifier as the dedupe key; multiple clicks on
    // the same row reset via `onAfterPrint` clearing `data` first.
    const key =
      data.dispenseNumber ?? `${data.patientName}:${data.medicineName}:${data.dispensedAt}`;
    if (lastFiredKey.current === key) return;
    lastFiredKey.current = key;

    const html = renderToStaticMarkup(<LabelBody data={data} />);

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 200);
      onAfterPrint();
    };

    const doc = iframe.contentDocument;
    if (!doc) {
      cleanup();
      return;
    }
    doc.open();
    doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Medicine Label</title>
<style>
  @page { size: 100mm 70mm; margin: 4mm; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; }
  body > * { width: 100mm; box-sizing: border-box; }
</style>
</head>
<body>${html}</body>
</html>`);
    doc.close();

    const onLoad = () => {
      const w = iframe.contentWindow;
      if (!w) {
        cleanup();
        return;
      }
      const onAfter = () => {
        w.removeEventListener('afterprint', onAfter);
        cleanup();
      };
      w.addEventListener('afterprint', onAfter);
      try {
        w.focus();
        w.print();
      } catch {
        cleanup();
      }
      // Watchdog in case `afterprint` never fires.
      setTimeout(() => {
        try {
          w.removeEventListener('afterprint', onAfter);
        } catch {
          // ignore
        }
        cleanup();
      }, 60000);
    };

    if (iframe.contentDocument?.readyState === 'complete') {
      onLoad();
    } else {
      iframe.addEventListener('load', onLoad, { once: true });
      setTimeout(onLoad, 0);
    }
  }, [data, onAfterPrint]);

  return null;
}
